import { Component, OnInit, EventEmitter, Input, Output } from "@angular/core";
import { LoginService } from "../login.service";
import { NO_SEND_HMOS, OTHER_HMOS_SCOPE } from "../exams.service";

// What the reports search asks for: a date range and a scope.
//   'AllReports' - everything done in the period
//   'HMO'        - everything done for one HMO
//   'StatusHMO'  - the exams of one HMO (or all of them) that have a problem:
//                  no PRN, not signed, not sent. Which of those to list is
//                  chosen on the results themselves, not here.
export type ReportSearchMode = "AllReports" | "HMO" | "StatusHMO";

// What both HMO selects offer: the four HMOs reports are actually sent to, and
// the group covering everything else - private, IDF, the unassigned. The status
// select adds "All" on top, for the whole problem list in one go.
const HMO_OPTIONS = [
  "Clalit",
  "Meuhedet",
  "Leumit",
  "Macabi",
  OTHER_HMOS_SCOPE,
];

export interface ReportSearchCriteria {
  start: any;
  end: any;
  mode: ReportSearchMode;
  hmo: string;
}

@Component({
  selector: "app-searchbox",
  templateUrl: "./searchbox.component.html",
  styleUrls: ["./searchbox.component.scss"],
  standalone: false,
})
export class SearchboxComponent implements OnInit {
  @Output() closed = new EventEmitter<any[]>();
  @Output() closeReports = new EventEmitter<ReportSearchCriteria>();
  @Input("startdate") startdate: Date;
  @Input("enddate") enddate: Date;
  @Input("exams") exams;
  @Input("examtype") etype = "All";
  @Input("reportstype") reportstype = "AllReports";
  @Input("hmostype") hmostype = "Macabi";

  @Input("isGastro") isGastro;
  @Input("isReports") isReports;
  // Criteria of the previous report search (if any), so the panel re-opens on it.
  @Input("lastreport") lastReport;
  searchmin = null;
  searchmax = null;
  reportHmo = "Clalit";
  // Kept apart from reportHmo: the status search offers only the HMOs reports
  // are sent to, plus "all of them", so one select's choice can never leave the
  // other showing a value it does not list.
  statusHmo = "All";
  reportMode: ReportSearchMode = "AllReports";

  cansearch = true;

  constructor(public ls: LoginService) {}

  ngOnInit() {
    if (this.isReports) {
      const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
      this.searchmax = yesterday;
      this.searchmin = yesterday;

      if (this.lastReport) {
        this.startdate = this.lastReport.start ? new Date(this.lastReport.start) : yesterday;
        this.enddate = this.lastReport.end ? new Date(this.lastReport.end) : yesterday;
        this.restoreMode(this.lastReport);
      } else {
        this.startdate = yesterday;
        this.enddate = yesterday;
      }
    }
  }

  // Report searches used to be one radio per failure case ('Refferelnum',
  // 'NotSigned', 'NotSent', 'Sent'), each with its own HMO select. A search
  // remembered from before that change re-opens as a status search on the same
  // HMO, which is where those cases live now.
  private restoreMode(last: any) {
    const hmo = last.reportHmo || last.sentHmo || last.hmo;
    // Both selects offer the four HMOs reports are sent to plus the group that
    // covers the rest, so a remembered Private / IDF / unassigned search
    // re-opens on that group. Anything else falls back to the default rather
    // than leaving a select blank on a value it does not list.
    const option = HMO_OPTIONS.includes(hmo)
      ? hmo
      : NO_SEND_HMOS.includes(hmo)
        ? OTHER_HMOS_SCOPE
        : null;
    switch (last.mode) {
      case "AllReports":
        this.reportMode = "AllReports";
        break;
      case "HMO":
        this.reportMode = "HMO";
        if (option) this.reportHmo = option;
        break;
      // 'StatusHMO', plus the old per-case modes ('Refferelnum', 'NotSigned',
      // 'NotSent', 'Sent') whose cases now live on the status results.
      case "StatusHMO":
      case "Refferelnum":
      case "NotSigned":
      case "NotSent":
      case "Sent":
        this.reportMode = "StatusHMO";
        // Only the status select offers "all of them", and it is the safe
        // default for anything unrecognised.
        this.statusHmo = option || "All";
        break;
      default:
        this.reportMode = "AllReports";
    }
  }

  get reportSearchHmo(): string {
    if (this.reportMode === "HMO") return this.reportHmo;
    if (this.reportMode === "StatusHMO") return this.statusHmo;
    return "All";
  }

  search() {
    if (this.isReports) {
      const criteria: ReportSearchCriteria = {
        start: this.startdate,
        end: this.enddate,
        mode: this.reportMode,
        hmo: this.reportSearchHmo,
      };
      return this.closeReports.emit(criteria);
    }

    this.closed.emit([this.startdate, this.enddate, this.exams, this.etype]);
  }

  setVals(mode: string) {
    switch (mode) {
      case "All":
        for (let e in this.exams) {
          this.exams[e] = true;
        }
        break;
      case "Gastro":
        for (let e in this.exams) {
          this.exams[e] = true;
        }
        this.exams.Gynecology = false;
        break;
    }
    this.cansearch = this.hasExam();
  }

  setE(event, exam: string) {
    if (event.srcElement.type != "checkbox") return;
    this.exams[exam] = !this.exams[exam];
    this.cansearch = this.hasExam();
  }

  hasExam(): boolean {
    if (!this.exams) return false;
    for (let e in this.exams) if (this.exams[e]) return true;
    //if nothing
    return false;
  }
}
